import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    googleMapsLoaded: boolean;
    googleMapsFailed: boolean;
    initGoogleMaps?: () => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService {
  private scriptLoaded = false;
  private scriptLoading = false;

  constructor() {
    // Initialize window flags if they don't exist
    if (typeof window.googleMapsLoaded === 'undefined') {
      window.googleMapsLoaded = false;
    }
    if (typeof window.googleMapsFailed === 'undefined') {
      window.googleMapsFailed = false;
    }
  }

  /**
   * Check if Google Maps should be loaded based on current route
   */
  private shouldLoadGoogleMaps(): boolean {
    const path = window.location.pathname;
    const href = window.location.href;

    // Don't load if opened in a popup/opener context (unless on specific routes)
    if (window.opener) {
      return false;
    }

    // Load on map-related routes
    return href.indexOf('/map') >= 0
      || href.indexOf('/timeline') >= 0
      || path.indexOf('/location/') >= 0
      || path.indexOf('/annotate/') >= 0
      || path.indexOf('/investigator/') >= 0
      || path.indexOf('/case/') >= 0
      || path.indexOf('/case-print/') >= 0;
  }

  /**
   * Load Google Maps API script dynamically
   */
  public loadGoogleMaps(): Promise<void> {
    return new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (window.googleMapsLoaded && typeof google !== 'undefined') {
        resolve();
        return;
      }

      // If already loading, wait for it
      if (this.scriptLoading) {
        const checkInterval = setInterval(() => {
          if (window.googleMapsLoaded) {
            clearInterval(checkInterval);
            resolve();
          } else if (window.googleMapsFailed) {
            clearInterval(checkInterval);
            reject(new Error('Google Maps failed to load'));
          }
        }, 100);
        return;
      }

      // Check if we should load Google Maps
      if (!this.shouldLoadGoogleMaps()) {
        window.googleMapsLoaded = false;
        window.googleMapsFailed = false;
        resolve(); // Resolve without loading
        return;
      }

      // Check if offline
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.warn('Offline: skipping Google Maps script load.');
        window.googleMapsLoaded = false;
        window.googleMapsFailed = true;
        reject(new Error('Offline: cannot load Google Maps'));
        return;
      }

      // Check if API key is configured
      if (!environment.googleMapsApiKey) {
        console.error('Google Maps API key is not configured in environment');
        window.googleMapsLoaded = false;
        window.googleMapsFailed = true;
        reject(new Error('Google Maps API key is not configured'));
        return;
      }

      // Check if script already exists in DOM
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        this.scriptLoading = true;
        // Wait for existing script to load
        existingScript.addEventListener('load', () => {
          window.googleMapsLoaded = true;
          window.googleMapsFailed = false;
          this.scriptLoading = false;
          this.scriptLoaded = true;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          window.googleMapsLoaded = false;
          window.googleMapsFailed = true;
          this.scriptLoading = false;
          reject(new Error('Google Maps failed to load'));
        });
        return;
      }

      // Load the script
      this.scriptLoading = true;
      console.log('Loading Map Library...');

      const lang = localStorage.getItem('languageSelected') || 'en';
      const googleSrc = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=geometry&loading=async&region=JP&language=${lang}`;

      const script = document.createElement('script');
      script.src = googleSrc;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        console.log('Google Maps API loaded successfully.');
        window.googleMapsLoaded = true;
        window.googleMapsFailed = false;
        this.scriptLoading = false;
        this.scriptLoaded = true;

        // Call initGoogleMaps if it exists
        if (typeof window.initGoogleMaps === 'function') {
          window.initGoogleMaps();
        }

        resolve();
      };

      script.onerror = () => {
        console.error('Google Maps failed to load.');
        window.googleMapsLoaded = false;
        window.googleMapsFailed = true;
        this.scriptLoading = false;
        reject(new Error('Google Maps failed to load'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Check if Google Maps is loaded
   */
  public isGoogleMapsLoaded(): boolean {
    return window.googleMapsLoaded && typeof google !== 'undefined';
  }

  /**
   * Wait for Google Maps to be available
   */
  public waitForGoogleMaps(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isGoogleMapsLoaded()) {
        resolve();
        return;
      }

      // Try to load if not already loading
      if (!this.scriptLoading && !this.scriptLoaded) {
        this.loadGoogleMaps().then(resolve).catch(reject);
        return;
      }

      // Wait for existing load
      const checkInterval = setInterval(() => {
        if (this.isGoogleMapsLoaded()) {
          clearInterval(checkInterval);
          resolve();
        } else if (window.googleMapsFailed) {
          clearInterval(checkInterval);
          reject(new Error('Google Maps failed to load'));
        }
      }, 100);

      // Timeout after 30 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!this.isGoogleMapsLoaded()) {
          reject(new Error('Google Maps load timeout'));
        }
      }, 30000);
    });
  }
}

